import { create } from 'zustand'

/**
 * Open-state for app-level UI panels (dialogs, drawers, side panels).
 *
 * ### Convention for new panels
 *
 * For each panel `<X>`, expose three properties on this store:
 *   - `is<X>Open: boolean`           — current open state
 *   - `set<X>Open(open: boolean)`    — programmatic setter (no persist)
 *   - `toggle<X>()`                  — user-driven toggle (persists if applicable)
 *
 * Persistence is opt-in per panel. `aiChatClosed` is persisted to
 * localStorage keyed on `location.pathname` because chat open-state
 * is graph-specific. Project Notes is intentionally session-only.
 *
 * Don't bundle non-open-state panel concerns (selected items, focused
 * tab, etc.) here — keep those in the panel's own slice or a domain
 * store. This file is for boolean visibility only.
 */

const STORAGE_KEY = 'aiChatClosed'

const readClosedMap = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch {
        return {}
    }
}

const writeClosedMap = map => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
    } catch {
    }
}

export const usePanelStore = create((set, get) => ({
    isProjectNotesOpen: false,
    isAiChatOpen: false,
    isMobileFilterOptionsOpen: false,

    setProjectNotesOpen: open => set({ isProjectNotesOpen: !!open }),

    toggleProjectNotesPanel: () =>
        set(state => ({ isProjectNotesOpen: !state.isProjectNotesOpen })),

    setMobileFilterOptionsOpen: open =>
        set({ isMobileFilterOptionsOpen: !!open }),

    toggleMobileFilterOptions: () =>
        set(state => ({
            isMobileFilterOptionsOpen: !state.isMobileFilterOptionsOpen,
        })),

    setAiChatOpen: (open, { persist = false } = {}) => {
        const next = !!open
        set({ isAiChatOpen: next })
        if (persist) {
            const map = readClosedMap()
            const pathname = window.location.pathname
            if (next) {
                if (map[pathname]) {
                    delete map[pathname]
                    writeClosedMap(map)
                }
            } else {
                map[pathname] = true
                writeClosedMap(map)
            }
        }
    },

    toggleAiChat: () => {
        get().setAiChatOpen(!get().isAiChatOpen, { persist: true })
    },
}))
